import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'text';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
  children: React.ReactNode;
  isLoading?: boolean;
  loadingText?: string;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'outline',
  size = 'md',
  icon,
  iconPosition = 'left',
  className = '',
  children,
  isLoading = false,
  loadingText,
  ...props
}) => {
  const baseStyles = 'flex items-center justify-center rounded-full transition-colors';

  const variantStyles = {
    primary: 'bg-cyan-500 text-white hover:bg-cyan-600 border border-cyan-500',
    secondary: 'bg-white text-cyan-500 border border-cyan-500 hover:bg-cyan-50',
    outline: 'bg-white border hover:bg-gray-50',
    text: 'hover:bg-gray-100',
  };

  const sizeStyles = {
    sm: 'px-3 py-1 text-sm gap-1',
    md: 'px-4 py-2 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2',
  };

  const iconClass = `${iconPosition === 'right' ? 'order-last' : ''}`;

  return (
    <button
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className} ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {loadingText || children}
        </>
      ) : (
        <>
          {icon && <span className={iconClass}>{icon}</span>}
          {children}
        </>
      )}
    </button>
  );
};

export default Button;
